<template>
  <OrderCard :title="title" class="gap-6 grid-cols-1">
    <template #top-right>
      <Dialog>
        <DialogTrigger as-child>
          <Button variant="ghost" size="icon" class="hover:text-primary">
            <Info class="size-5" />
          </Button>
        </DialogTrigger>
        <DialogContent class="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {{ isPickup ? t("order.info.pickupInfo") : t("order.info.deliveryTimeInfo") }}
            </DialogTitle>
          </DialogHeader>
          <div class="text-sm text-muted-foreground space-y-2">
            <p>{{ isPickup ? t("order.info.pickupHours") : t("order.info.deliveryTimeDetails") }}</p>
          </div>
        </DialogContent>
      </Dialog>
    </template>
    <FormField name="preffered_date">
      <FormItem class="col-span-full flex flex-col gap-2">
        <FormLabel>
          {{ isPickup ? t("order.formLabels.pickupDate") : t("order.formLabels.deliveryDate") }}
        </FormLabel>
        <FormControl>
          <div class="flex items-center gap-2">
            <Button type="button" variant="outline" size="icon" class="shrink-0"
              :aria-label="t('order.calendar.prevMonth')" @click="scroll(-1)">
              <ChevronLeft class="size-4" />
            </Button>

            <div ref="scroller"
              class="flex flex-1 gap-2 overflow-x-auto scroll-smooth snap-x snap-mandatory py-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              <button v-for="day in dayOptions" :key="day.toString()" type="button" :data-date="day.toString()" :class="cn(
                'snap-start shrink-0 w-[4.5rem] flex flex-col items-center gap-1 rounded-xl border px-2 py-3 text-center transition-colors',
                isSelected(day)
                  ? 'border-blueberry-dark bg-blueberry-dark text-white'
                  : 'border-input bg-background hover:bg-accent hover:text-accent-foreground',
              )" @click="selectDate(day)">
                <span class="text-[11px] font-medium uppercase leading-none opacity-80">
                  {{ isToday(day) ? t("order.formLabels.today") : weekdayLabel(day) }}
                </span>
                <span class="text-base font-semibold leading-none">{{ dayNumber(day) }}</span>
                <span class="text-[11px] leading-none opacity-80">{{ monthLabel(day) }}</span>
              </button>
            </div>

            <Button type="button" variant="outline" size="icon" class="shrink-0"
              :aria-label="t('order.calendar.nextMonth')" @click="scroll(1)">
              <ChevronRight class="size-4" />
            </Button>
          </div>
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField v-if="showTime" v-slot="{ value: timeValue, setValue: setTimeValue }" name="preffered_time">
      <FormItem class="col-span-full flex flex-col gap-2">
        <FormLabel>
          {{ isPickup ? t("order.formLabels.pickupTime") : t("order.formLabels.preferredTime") }}
        </FormLabel>
        <div class="grid w-full grid-cols-2 gap-4">
          <div class="flex min-w-0 flex-col gap-2">
            <FormControl>
              <Select :model-value="splitTime(timeValue).h"
                @update:model-value="(h) => onHourChange(String(h), timeValue, setTimeValue)">
                <SelectTrigger class="w-full !h-10">
                  <SelectValue :placeholder="t('order.formLabels.hour')" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="h in availableHours" :key="h" :value="h">
                    {{ h }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
          </div>
          <div class="flex min-w-0 flex-col gap-2">
            <FormControl>
              <Select :model-value="splitTime(timeValue).m"
                @update:model-value="(m) => onMinuteChange(String(m), timeValue, setTimeValue)">
                <SelectTrigger class="w-full !h-10">
                  <SelectValue :placeholder="t('order.formLabels.minute')" />
                </SelectTrigger>
                <SelectContent class="max-h-60">
                  <SelectItem
                    v-for="m in getAvailableMinutesForHour(splitTime(timeValue).h || availableHours[0] || '')"
                    :key="m" :value="m">
                    {{ m }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
          </div>
        </div>
        <FormMessage />
      </FormItem>
    </FormField>
  </OrderCard>
</template>

<script setup lang="ts">
import type { DateValue } from "@internationalized/date"
import {
  DateFormatter,
  parseDate,
  today,
} from "@internationalized/date"
import { ChevronLeft, ChevronRight, Info } from "lucide-vue-next"
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue"
import { useFormContext } from "vee-validate"
import { useI18n } from "vue-i18n"
import { OrderCard } from "../../card"
import { Button } from "../../button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../dialog"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../select"
import { cn } from "../../../lib/utils"

const { t, locale } = useI18n()
const orderTimeZone = "Europe/Warsaw"

const props = withDefaults(
  defineProps<{
    title: string
    isPickup?: boolean
    /** Show the preferred-time picker (office deliveries and pickups only). */
    showTime?: boolean
    openingTime?: string
    closingTime?: string
    minuteStep?: number
    /** Number of days rendered in the carousel. */
    days?: number
    /** At or after this local HH:MM time, the window starts tomorrow. */
    cutoffTime?: string
    /** ISO dates that should be omitted from delivery date choices. */
    blockedDates?: string[]
  }>(),
  {
    isPickup: false,
    showTime: false,
    days: 7,
    cutoffTime: "15:00",
    blockedDates: () => [],
  },
)

const form = useFormContext()

const dateFormatLocale = computed(() => {
  const l = locale.value?.toLowerCase() ?? "en"
  if (l.startsWith("pl"))
    return "pl-PL"
  if (l.startsWith("en"))
    return "en-US"
  return locale.value || "en-US"
})

const weekdayFormatter = computed(
  () => new DateFormatter(dateFormatLocale.value, { weekday: "short" }),
)
const monthFormatter = computed(
  () => new DateFormatter(dateFormatLocale.value, { month: "short" }),
)

const currentTime = ref(new Date())
let clockInterval: ReturnType<typeof setInterval> | undefined

onMounted(() => {
  currentTime.value = new Date()
  clockInterval = setInterval(() => {
    currentTime.value = new Date()
  }, 30_000)
})

onBeforeUnmount(() => {
  if (clockInterval !== undefined)
    clearInterval(clockInterval)
})

function cutoffMinutes(): number {
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(props.cutoffTime))
    return 15 * 60
  return timeToMinutes(props.cutoffTime)
}

function currentMinutesInOrderTimeZone(value: Date): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: orderTimeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(value)
  const hour = Number(parts.find(part => part.type === "hour")?.value ?? 0)
  const minute = Number(parts.find(part => part.type === "minute")?.value ?? 0)
  return hour * 60 + minute
}

/** First selectable day: today, unless we are at or past the cutoff, then tomorrow. */
const startDate = computed<DateValue>(() => {
  const base = today(orderTimeZone)
  const now = currentTime.value
  const pastCutoff = currentMinutesInOrderTimeZone(now) >= cutoffMinutes()
  return pastCutoff ? base.add({ days: 1 }) : base
})

const blockedDateSet = computed(() => new Set(props.blockedDates))

const dayOptions = computed<DateValue[]>(() => {
  const candidateCount = props.days + blockedDateSet.value.size
  return Array.from(
    { length: candidateCount },
    (_, i) => startDate.value.add({ days: i }),
  )
    .filter(day => !blockedDateSet.value.has(day.toString()))
    .slice(0, props.days)
})

function isSelected(day: DateValue): boolean {
  return form.values.preffered_date === day.toString()
}

function selectDate(day: DateValue) {
  form.setFieldValue("preffered_date", day.toString())
}

function isToday(day: DateValue): boolean {
  return day.compare(today(orderTimeZone)) === 0
}

function weekdayLabel(day: DateValue): string {
  return weekdayFormatter.value.format(day.toDate(orderTimeZone))
}

function dayNumber(day: DateValue): number {
  return day.day
}

function monthLabel(day: DateValue): string {
  return monthFormatter.value.format(day.toDate(orderTimeZone))
}

/** Drop a stale selection that falls before the first selectable day (e.g. after the cutoff). */
watch(
  [startDate, blockedDateSet, () => form.values.preffered_date],
  () => {
    const s = form.values.preffered_date
    if (typeof s !== "string" || !s)
      return
    try {
      if (
        parseDate(s).compare(startDate.value) < 0
        || blockedDateSet.value.has(s)
      )
        form.setFieldValue("preffered_date", undefined, false)
    }
    catch {
      form.setFieldValue("preffered_date", undefined, false)
    }
  },
  { immediate: true },
)

const scroller = ref<HTMLElement>()

function scroll(direction: number) {
  const el = scroller.value
  if (!el)
    return
  el.scrollBy({ left: direction * Math.round(el.clientWidth * 0.8), behavior: "smooth" })
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

function splitTime(v: unknown): { h?: string; m?: string } {
  if (typeof v !== "string" || !/^\d{1,2}:\d{2}(?::\d{2})?$/.test(v.trim())) {
    return {}
  }

  const [a, b] = v.trim().split(":")
  const h = Math.min(23, Math.max(0, Number.parseInt(a, 10)))
  const m = Math.min(59, Math.max(0, Number.parseInt(b, 10)))

  return { h: pad2(h), m: pad2(m) }
}

function joinTime(h: string, m: string) {
  return `${h}:${m}`
}

function isValidTimeString(v: string): boolean {
  return /^([01]\d|2[0-3]):([0-5]\d)$/.test(v)
}

function timeToMinutes(v: string): number {
  const [h, m] = v.split(":").map(Number)
  return h * 60 + m
}

function minutesToTime(total: number): string {
  const h = Math.floor(total / 60)
  const m = total % 60
  return `${pad2(h)}:${pad2(m)}`
}

const openingMinutes = computed(() =>
  props.openingTime && isValidTimeString(props.openingTime)
    ? timeToMinutes(props.openingTime)
    : timeToMinutes("00:00"),
)

const closingMinutes = computed(() =>
  props.closingTime && isValidTimeString(props.closingTime)
    ? timeToMinutes(props.closingTime)
    : timeToMinutes("23:59"),
)

const safeMinuteStep = computed(() => {
  const step = props.minuteStep ? Number(props.minuteStep) : 1
  return Number.isInteger(step) && step > 0 ? step : 1
})

const allowedTimes = computed(() => {
  const start = openingMinutes.value
  const end = closingMinutes.value

  if (start > end) {
    return []
  }

  const result: string[] = []
  for (let minutes = start; minutes <= end; minutes += safeMinuteStep.value) {
    result.push(minutesToTime(minutes))
  }

  return result
})

const availableHours = computed(() => {
  const hours = new Set(allowedTimes.value.map(time => time.split(":")[0]))
  return Array.from(hours)
})

function getAvailableMinutesForHour(hour: string): string[] {
  return allowedTimes.value
    .filter(time => time.startsWith(`${hour}:`))
    .map(time => time.split(":")[1])
}

function onHourChange(
  hour: string,
  currentValue: unknown,
  setTimeValue: (v: string | undefined, shouldValidate?: boolean) => void,
) {
  const selectedHour = String(hour)
  const current = splitTime(currentValue)
  const allowedMinutes = getAvailableMinutesForHour(selectedHour)

  if (allowedMinutes.length === 0) {
    setTimeValue(undefined)
    return
  }

  const minute = current.m && allowedMinutes.includes(current.m)
    ? current.m
    : allowedMinutes[0]

  setTimeValue(joinTime(selectedHour, minute))
}

function onMinuteChange(
  minute: string,
  currentValue: unknown,
  setTimeValue: (v: string | undefined, shouldValidate?: boolean) => void,
) {
  const current = splitTime(currentValue)
  const hour = current.h || availableHours.value[0]

  if (!hour) {
    setTimeValue(undefined)
    return
  }

  const allowedMinutes = getAvailableMinutesForHour(hour)
  const selectedMinute = String(minute)

  if (!allowedMinutes.includes(selectedMinute)) {
    setTimeValue(joinTime(hour, allowedMinutes[0]))
    return
  }

  setTimeValue(joinTime(hour, selectedMinute))
}
</script>
